import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Save, Search, X } from "lucide-react";
import { ApiError } from "@/api/client";
import { useScenarios } from "@/api/scenarios";
import {
  useRunner,
  useRunners,
  useSaveScenarioSet,
  useScenarioSets,
  useStartSuite,
} from "@/api/suites";
import type { Runner, RunnerScenarioSummary, ScenarioSummary } from "@/api/types";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";

/**
 * Cloud affinity, for the one verdict the picker computes itself.
 *
 * The local runner's scenarios arrive with the server's own per-kind verdicts
 * attached, so those are used verbatim. A phone-home runner's catalog does not
 * carry verdicts — the home runner cannot run the check for a library it does
 * not hold until a suite is composed — so the rules are applied here from the
 * reported summary. Both paths are advisory: the server re-checks every item
 * before starting it, and the executing runner checks again on its own side.
 */
const BACKEND_CLOUD: Record<string, string | null> = {
  aws: "aws", azure: "azure", adx: "azure",
  grafana: null, datadog: null, dynatrace: null, splunk: null, solarwinds: null,
};

type Verdict = { runnable: boolean; code?: string; reason?: string };

function verdictFromSummary(s: RunnerScenarioSummary, kind: string | undefined): Verdict {
  if (!kind) return { runnable: true };
  const supported = s.supported_backends ?? [];
  if (supported.length > 0 && !supported.includes(kind)) {
    return {
      runnable: false,
      code: "backend_kind_unsupported",
      reason: `only supports ${supported.join(", ")}`,
    };
  }
  if (s.mapped_kinds !== null && !(s.mapped_kinds ?? []).includes(kind)) {
    return {
      runnable: false,
      code: "no_service_mapping_for_backend",
      reason: `no service mapping for ${kind}`,
    };
  }
  const target = BACKEND_CLOUD[kind] ?? null;
  if (s.cloud && target && s.cloud !== target) {
    return {
      runnable: false,
      code: "cloud_mismatch",
      reason: `written against ${s.cloud}; this backend is ${target}`,
    };
  }
  return { runnable: true };
}

/**
 * The local runner's scenarios, shaped like a reported catalog.
 *
 * `verdicts` carries the server's answer per backend kind, which is preferred
 * over recomputing: it is the same code path the run-start gate uses, so the
 * picker and the gate cannot disagree.
 */
type CatalogEntry = RunnerScenarioSummary & {
  verdicts?: Record<string, { runnable: boolean; code: string | null; reason: string }>;
};

function localCatalog(rows: ScenarioSummary[]): CatalogEntry[] {
  return rows.map((s) => ({
    slug: s.slug,
    name: s.name,
    description: s.description,
    cloud: s.cloud ?? null,
    supported_backends: [],
    mapped_kinds: null,
    verdicts: s.compatibility,
  }));
}

function verdictFor(s: CatalogEntry, kind: string | undefined): Verdict {
  if (!kind) return { runnable: true };
  const served = s.verdicts?.[kind];
  if (served) {
    return { runnable: served.runnable, code: served.code ?? undefined, reason: served.reason };
  }
  return verdictFromSummary(s, kind);
}

export function SuiteLaunchPage() {
  const navigate = useNavigate();
  const runners = useRunners();
  const scenarios = useScenarios();
  const sets = useScenarioSets();
  const startSuite = useStartSuite();
  const saveSet = useSaveScenarioSet();
  const permissions = usePermissions();

  const [runnerId, setRunnerId] = useState<string>("local");
  const [backendId, setBackendId] = useState<string>("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");
  const [name, setName] = useState("");
  const [concurrency, setConcurrency] = useState(2);
  const [stagger, setStagger] = useState(true);
  const [allowMismatch, setAllowMismatch] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setName_, setSetName] = useState("");

  const runner = useRunner(runnerId);
  const selectedRunner: Runner | undefined = runner.data;

  // Changing the destination invalidates the picks that were legal under the
  // old one. Rather than silently carrying an impossible selection forward,
  // drop the ones that no longer fit and say how many went.
  const backends = selectedRunner?.backends ?? [];
  const backendKind = backends.find((b) => b.id === backendId)?.kind;

  const catalog: CatalogEntry[] = useMemo(() => {
    if (!selectedRunner) return [];
    return selectedRunner.is_local
      ? localCatalog(scenarios.data ?? [])
      : (selectedRunner.scenarios ?? []);
  }, [selectedRunner, scenarios.data]);

  useEffect(() => {
    if (backends.length > 0 && !backends.some((b) => b.id === backendId)) {
      setBackendId(backends[0].id);
    }
    if (backends.length === 0) setBackendId("");
  }, [backends, backendId]);

  const verdicts = useMemo(() => {
    const m = new Map<string, Verdict>();
    for (const s of catalog) m.set(s.slug, verdictFor(s, backendKind));
    return m;
  }, [catalog, backendKind]);

  const [droppedNote, setDroppedNote] = useState<string | null>(null);
  useEffect(() => {
    if (!backendKind || picked.size === 0) return;
    const keep = new Set([...picked].filter((slug) => {
      const v = verdicts.get(slug);
      return !v || v.runnable || (allowMismatch && v.code === "cloud_mismatch");
    }));
    if (keep.size !== picked.size) {
      setDroppedNote(
        `${picked.size - keep.size} scenario(s) removed — they cannot run on this backend.`,
      );
      setPicked(keep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendKind, allowMismatch, verdicts]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return catalog
      .filter((s) => !q || s.slug.toLowerCase().includes(q)
        || (s.description ?? "").toLowerCase().includes(q))
      .sort((a, b) => a.slug.localeCompare(b.slug));
  }, [catalog, query]);

  const runnableShown = shown.filter((s) => {
    const v = verdicts.get(s.slug);
    return !v || v.runnable || (allowMismatch && v.code === "cloud_mismatch");
  });

  function toggle(slug: string) {
    setDroppedNote(null);
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(slug) ? next.delete(slug) : next.add(slug);
      return next;
    });
  }

  async function launch() {
    setError(null);
    try {
      const suite = await startSuite.mutateAsync({
        backend_id: backendId,
        runner_id: runnerId,
        scenario_ids: [...picked],
        name: name.trim() || undefined,
        concurrency,
        stagger,
        allow_cloud_mismatch: allowMismatch,
      });
      navigate(`/suites/${suite.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function saveAsSet() {
    if (!setName_.trim() || picked.size === 0) return;
    setError(null);
    try {
      await saveSet.mutateAsync({ name: setName_.trim(), scenario_ids: [...picked] });
      setSetName("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  const offline = selectedRunner && !selectedRunner.online;
  const canLaunch = permissions.canMutate && !!backendId && picked.size > 0
    && !startSuite.isPending;

  return (
    <div className="p-6 space-y-5 max-w-6xl">
      <header>
        <h2 className="text-2xl font-semibold text-foreground">New suite</h2>
        <p className="text-sm text-muted-foreground">
          Pick where the telemetry lands, then which scenarios to send there.
        </p>
      </header>

      {/* ---- destination ---- */}
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4 border border-border rounded-lg p-4 bg-card">
        <div className="space-y-1.5">
          <Label htmlFor="runner">Runner</Label>
          <select
            id="runner"
            value={runnerId}
            onChange={(e) => { setRunnerId(e.target.value); setPicked(new Set()); }}
            className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm"
          >
            {(runners.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
                {r.cloud ? ` · ${r.cloud}` : ""}
                {r.is_local ? " (here)" : r.online ? "" : " — offline"}
              </option>
            ))}
          </select>
          {offline && (
            <p className="text-xs text-amber-500">
              Hasn't checked in recently. Work will queue and start when it calls home.
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="backend">Backend</Label>
          <select
            id="backend"
            value={backendId}
            onChange={(e) => setBackendId(e.target.value)}
            disabled={backends.length === 0}
            className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm disabled:opacity-50"
          >
            {backends.map((b) => (
              <option key={b.id} value={b.id}>{b.name} ({b.kind})</option>
            ))}
          </select>
          {backends.length === 0 && (
            <p className="text-xs text-muted-foreground">
              {selectedRunner?.is_local
                ? "No backends configured here."
                : "This runner reported no backends at its last check-in."}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="conc">At a time</Label>
          <Input
            id="conc" type="number" min={1} max={8} className="h-9"
            value={concurrency}
            onChange={(e) => setConcurrency(Math.max(1, Math.min(8, Number(e.target.value) || 1)))}
          />
          <p className="text-xs text-muted-foreground">
            2 is the value that holds; the runner sheds requests above it.
            Use 1 to keep scenarios from overlapping at all.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="stagger">Pacing</Label>
          <label htmlFor="stagger"
            className="flex items-center gap-2 h-9 text-sm cursor-pointer">
            <input
              id="stagger" type="checkbox" className="accent-primary"
              checked={stagger} onChange={(e) => setStagger(e.target.checked)}
            />
            Randomise start times
          </label>
          <p className="text-xs text-muted-foreground">
            Without this, stacks are created a constant distance apart — an
            investigation measured that spacing and identified the harness.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="sname">Name</Label>
          <Input
            id="sname" className="h-9" placeholder="optional"
            value={name} onChange={(e) => setName(e.target.value)}
          />
        </div>
      </section>

      {/* ---- scenarios ---- */}
      <section className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[16rem] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              className="pl-9 h-9" placeholder="Filter scenarios…"
              value={query} onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <Button variant="outline" size="sm"
            onClick={() => setPicked(new Set(runnableShown.map((s) => s.slug)))}>
            Select all runnable ({runnableShown.length})
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPicked(new Set())}>
            Clear
          </Button>
          {(sets.data ?? []).length > 0 && (
            <select
              defaultValue=""
              onChange={(e) => {
                const s = (sets.data ?? []).find((x) => x.id === e.target.value);
                if (s) setPicked(new Set(s.scenario_ids));
              }}
              className="h-9 rounded-md border border-border bg-background px-2 text-sm"
            >
              <option value="">Load a saved set…</option>
              {(sets.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.scenario_ids.length})
                </option>
              ))}
            </select>
          )}
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
            <input type="checkbox" className="accent-primary"
              checked={allowMismatch}
              onChange={(e) => setAllowMismatch(e.target.checked)} />
            Allow cross-cloud
          </label>
        </div>

        {droppedNote && (
          <p className="text-xs text-amber-500 flex items-center gap-1">
            {droppedNote}
            <button type="button" onClick={() => setDroppedNote(null)}><X size={11} /></button>
          </p>
        )}

        <div className="border border-border rounded-lg divide-y divide-border max-h-[26rem] overflow-auto bg-card">
          {shown.map((s) => {
            const v = verdicts.get(s.slug) ?? { runnable: true };
            const overridden = !v.runnable && allowMismatch && v.code === "cloud_mismatch";
            const blocked = !v.runnable && !overridden;
            return (
              <label
                key={s.slug}
                className={cn(
                  "flex items-start gap-2.5 px-3 py-2 text-sm",
                  blocked ? "opacity-55 cursor-not-allowed" : "cursor-pointer hover:bg-muted/40",
                )}
              >
                <input
                  type="checkbox" className="accent-primary mt-0.5"
                  checked={picked.has(s.slug)} disabled={blocked}
                  onChange={() => toggle(s.slug)}
                />
                <span className="min-w-0 flex-1">
                  <span className="font-mono text-foreground">{s.slug}</span>
                  {s.cloud && (
                    <span className="ml-2 rounded border border-sky-500/50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-sky-500">
                      {s.cloud}
                    </span>
                  )}
                  {overridden && (
                    <span className="ml-2 rounded border border-amber-500/50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-500">
                      cross-cloud
                    </span>
                  )}
                  {blocked && v.reason && (
                    <span className="block text-xs text-muted-foreground">{v.reason}</span>
                  )}
                </span>
              </label>
            );
          })}
          {shown.length === 0 && (
            <p className="px-3 py-6 text-sm text-muted-foreground text-center">
              {scenarios.isLoading || runner.isLoading
                ? "Loading…"
                : "No scenarios reported for this runner."}
            </p>
          )}
        </div>
      </section>

      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Input
            className="h-9 w-52" placeholder="Save selection as…"
            value={setName_} onChange={(e) => setSetName(e.target.value)}
          />
          <Button variant="outline" onClick={saveAsSet}
            disabled={!setName_.trim() || picked.size === 0 || saveSet.isPending}>
            <Save size={14} /> Save set
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{picked.size} selected</span>
          <Button onClick={launch} disabled={!canLaunch}
            title={!permissions.canMutate ? (permissions.mutationReason ?? "") : undefined}>
            <Play size={14} /> {startSuite.isPending ? "Starting…" : "Start suite"}
          </Button>
        </div>
      </div>
    </div>
  );
}
